import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { App as CapacitorApp } from '@capacitor/app';

import i18n from '../i18n';
import { checkForUpdate, applyPendingUpdate, UpdateStatus } from '../utils/appUpdate';
import { isNativeApp } from '../utils/positionSource';
import { openModal, closeModal } from '../state/actions/ui/modal';
import { modalIds, ModalStateType } from '../components/modals/SuperModal/types';
import { getCurrentModal } from '../state/selectors/ui/modal';
import { isBlocking, isQuiet, quietMsFor } from '../utils/updateNagState';
import {
  readNag,
  recordDismissal,
  resolveMaxDismissals,
  resolveUnitMs,
} from '../utils/updateNagStorage';
import { versionManager } from '../utils/versionManager';
import { CHECK_UPDATE_EVENT, isWebUpdateEnabled } from '../utils/webVersion';
// La versión que corre ESTE bundle. Con ella se sabe si la tablet se ha
// actualizado desde que empezó a contar el plazo (ver `readNag`).
import { version as RUNNING_WEB_VERSION } from '../../package.json';

/**
 * Orquesta la actualización de la app nativa **y de la web**.
 *
 * ─── En web (desde el 2026-10-01) ───────────────────────────────────────────
 *
 * La fuente es `/version.json` del propio dominio (va en el mismo despliegue
 * que los JS, así que solo anuncia lo que ya se sirve) y aplicar es recargar
 * sin borrar trabajo (`versionManager.applyWebUpdate`). Todo lo demás es la
 * MISMA política que en la app: nunca con tramo propio ni encima de otra
 * ventana, cerrar aplaza con la escalera, obligatorio al 5.º cierre o a las
 * 24 h, o desde el primero si la release es forzada. Solo en producción (o con
 * el interruptor de pruebas `rois.update.webChecks`). Antes la web tenía su
 * propio aviso en `versionManager`: sin guardas, la X aceptaba y aceptar
 * borraba las colas sin conexión (spec 2026-09-30).
 *
 * ─── En la app: qué ve el operario ───────────────────────────────────────
 *
 * Sin recorrido en curso, sale el MISMO modal que en la web ("Nueva versión
 * disponible") y al aceptar la app se reinicia en un segundo. Si lo que cambió
 * fue la capa nativa, el botón descarga e instala el APK — con la pantalla de
 * Android de por medio, que no se puede evitar sin MDM.
 *
 * CON un tramo de recorrido en curso: **no se ofrece nada**, y si ya estaba
 * abierto se cierra. En cuanto el tramo se cierra —el operario pulsa Finalizar,
 * que lo hace de todas formas varias veces por turno— se ofrece. Reiniciar el
 * WebView durante una captura es exactamente el fallo que la app nativa viene a
 * evitar: el servicio de GPS sobreviviría (graba en SQLite), pero el cronómetro
 * y el mapa en vivo se cortarían delante de él.
 *
 * ─── Por qué preguntar y no aplicar solo ───────────────────────────────────
 *
 * Antes se aplicaba sin avisar. Un reinicio inesperado, aunque dure un segundo,
 * a un operario que está rellenando un parte le parece que la app se ha caído.
 * Y hace falta un aviso de todas formas para el caso del APK, que no se puede
 * automatizar. Un solo camino, entendible, para los tres casos.
 *
 * ─── Si cierra el aviso no se pierde ───────────────────────────────────────
 *
 * Se vuelve a offerUpdate en la siguiente comprobación (no antes: ver
 * `QUIET_AFTER_DISMISS_MS`, que evita convertir el aviso en un acoso cada vez
 * que la app vuelve a primer plano), y también al siguiente arranque de la app:
 * el bundle sigue descargado, así que aceptar solo cuesta un reinicio.
 *
 * Lo que NO pasa es que se aplique a solas. Es deliberado y es el precio de la
 * garantía: mientras no esté encolado en el plugin, no hay forma de que el
 * sistema lo active por su cuenta con el operario a mitad de un tramo.
 */

/**
 * Cada cuánto se comprueba, en unidades de la escalera: un cuarto. En
 * producción la unidad es una hora → 15 min, como siempre.
 *
 * 🔴 En unidades y no en milisegundos fijos por la escalera de prueba
 * (1 h = 1 min): con 15 min fijos, los silencios de 12, 6 y 4 min quedaban
 * tapados por el intervalo y la prueba con la app abierta no se parecía a
 * producción. Así guarda la misma proporción: 15 s en la prueba.
 */
const CHECK_INTERVAL_UNITS = 0.25;

/** Suelo para que una unidad mal puesta no convierta esto en un bucle. */
const MIN_CHECK_INTERVAL_MS = 5_000;

/** APK siempre; web solo en producción (o con el interruptor de pruebas). */
const updatesEnabled = (): boolean => isNativeApp() || isWebUpdateEnabled();

interface UseAppUpdateOptions {
  /**
   * Si hay un tramo de recorrido corriendo. Es la ÚNICA salvaguarda que impide
   * reiniciar la app a mitad de una captura.
   */
  hasActiveSegment: boolean;
}

export function useAppUpdate({ hasActiveSegment }: UseAppUpdateOptions): void {
  const dispatch = useDispatch();
  // Solo el id, no el objeto entero: este hook vive en el `TimerOverlay`, que
  // está montado en el Header de TODA la app. Suscribirse al modal completo lo
  // re-renderizaría cada vez que cualquier modal cambia un dato interno.
  const currentModalId = useSelector(
    (state: unknown) => (getCurrentModal(state) as ModalStateType | undefined)?.id
  );

  // Refs además de las props: el temporizador y el listener de `resume` se
  // crean una vez y leerían valores congelados.
  const activeRef = useRef(hasActiveSegment);
  activeRef.current = hasActiveSegment;

  const statusRef = useRef<UpdateStatus | null>(null);
  // Quién abrió el modal. Sin esto, cerrarlo al empezar un tramo cerraría
  // también cualquier otro modal que el operario tuviera abierto.
  const openedByUs = useRef(false);
  const offeredVersion = useRef<string | null>(null);
  /**
   * Respaldo del silencio en MEMORIA.
   *
   * 🔴 `localStorage` puede estar bloqueado por política del dispositivo o sin
   * cuota: entonces `write()` se lo traga, `readNag` vuelve a sembrar
   * `dismissals: 0` en cada llamada y el aviso no se callaría nunca — saldría
   * en cada comprobación. Antes esto no pasaba porque el silencio vivía en
   * memoria; al persistirlo se perdió ese suelo. Se guardan los dos y manda el
   * más lejano.
   */
  const quietUntilFallback = useRef<Record<string, number>>({});
  // Qué modal hay en pantalla AHORA. En ref porque `offerUpdate` se llama desde un
  // temporizador y desde el listener de `resume`, que leerían un valor
  // congelado del render en que se crearon.
  const visibleModalId = useRef<string | null | undefined>(currentModalId);
  visibleModalId.current = currentModalId;
  // Evita que dos comprobaciones se solapen. Pasa de verdad: el operario apaga
  // y enciende la pantalla varias veces mientras se descarga un bundle de
  // varios MB, y cada `resume` dispara una comprobación.
  const checking = useRef(false);

  const closeIfMine = useCallback(() => {
    if (!openedByUs.current) return;
    openedByUs.current = false;
    offeredVersion.current = null;
    dispatch(closeModal());
  }, [dispatch]);

  const offerUpdate = useCallback(
    (status: UpdateStatus) => {
      if (
        status.kind !== 'web-ready' &&
        status.kind !== 'native-required' &&
        status.kind !== 'web-available'
      ) {
        return;
      }

      // 🔴 Se siembra el estado ANTES de las guardas de abajo, no después. El
      // plazo de 24 unidades existe justo para la tablet que NUNCA llega a ver
      // el aviso —siempre hay un tramo o un modal por encima—, así que si el
      // reloj solo arrancara cuando ya se puede enseñar, no cubriría el caso
      // para el que se escribió.
      const now = Date.now();
      const unitMs = resolveUnitMs();
      const { state, quietUntil } = readNag(status.version, now, RUNNING_WEB_VERSION);

      if (activeRef.current) return; // con tramo, ni una palabra
      if (openedByUs.current) return;

      // 🔴 Nunca por encima de otro modal. `openModal` REEMPLAZA el que hubiera
      // —el reducer sustituye id y data—, así que offerUpdate aquí le arrancaría al
      // operario de debajo el modal que estuviera usando: cambiar sentido,
      // vincular un tramo, la actuación básica… y en el RV casi todo pasa por
      // ahí. Se espera al siguiente ciclo; la actualización no tiene ninguna
      // prisa comparada con lo que él esté rellenando.
      if (
        visibleModalId.current &&
        visibleModalId.current !== modalIds.VERSION_UPDATE_MODAL
      ) {
        return;
      }

      const mode =
        status.kind === 'web-ready'
          ? 'native-bundle'
          : status.kind === 'native-required'
            ? 'native-apk'
            : 'web';
      const blocking = isBlocking({
        state,
        now,
        mode,
        mandatory: status.mandatory,
        unitMs,
        maxDismissals: resolveMaxDismissals(),
      });

      // Un aviso que ya es obligatorio no se calla: el silencio existe para lo
      // que todavía se puede aplazar.
      // Manda el más lejano de los dos: el persistido y el de memoria.
      const silencio = Math.max(
        quietUntil ?? 0,
        quietUntilFallback.current[status.version] ?? 0
      );
      if (!blocking && isQuiet(silencio || null, now, unitMs)) return;

      openedByUs.current = true;
      offeredVersion.current = status.version;

      dispatch(
        openModal(modalIds.VERSION_UPDATE_MODAL, {
          // La huella no se enseña: para el operario no significa nada. En el
          // camino del APK el número bueno es el de la app, que además es el
          // que verá en Ajustes → Aplicaciones al terminar de instalar.
          version:
            status.kind === 'native-required' ? status.webVersion : status.version,
          reason: i18n.t('errors:newVersionDetected'),
          isNewVersion: true,
          mode,
          // Obligatoria = no se puede closeIfMine. La salida es el botón, que
          // siempre está: descargar el paquete o reiniciar la app. Lo decide la
          // escalera, o el `mandatory` del manifiesto si viene puesto.
          blocking,
          ...(status.kind === 'native-required'
            ? { apkUrl: status.apkUrl, apkChecksum: status.apkChecksum }
            : {}),
          onContinue: () => {
            if (status.kind === 'web-available') {
              // Web: recargar no puede fallar a medias como una descarga, así
              // que no hace falta la salida de emergencia de la APK. Se baja
              // `openedByUs` antes para que la recarga no cuente como cierre.
              openedByUs.current = false;
              void versionManager.applyWebUpdate();
              return;
            }
            if (status.kind !== 'web-ready') return;
            openedByUs.current = false;
            void applyPendingUpdate(status.bundleId, activeRef.current).then(
              (aplicada) => {
                // Si se aplica, el WebView se reinicia y esto no llega a correr.
                // Si NO se aplica —el bundle descargado ya no está, el plugin
                // rechaza—, con `blocking` el operario se queda encerrado: el
                // cierre del modal está vetado y el botón no hace nada visible.
                // 🔴 Un bloqueo solo se justifica si su salida funciona, así que
                // aquí se cierra a la fuerza. La siguiente comprobación lo
                // vuelve a ofrecer, y mientras tanto la tablet se puede usar.
                if (!aplicada) dispatch(closeModal());
              }
            );
          },
        })
      );
    },
    [dispatch]
  );

  const runCheck = useCallback(async () => {
    // Reentrada: `checkForUpdate` puede estar DESCARGANDO varios MB. Dos
    // llamadas solapadas serían dos descargas del mismo bundle.
    if (checking.current) return;
    checking.current = true;
    let next: UpdateStatus;
    try {
      next = await checkForUpdate();
    } finally {
      checking.current = false;
    }
    statusRef.current = next;
    // Una comprobación nueva reabre la conversación: si el operario cerró el
    // aviso hace un rato, aquí ya ha pasado el silencio y se le vuelve a
    // offerUpdate.
    offerUpdate(next);
  }, [offerUpdate]);

  useEffect(() => {
    if (!updatesEnabled()) return;
    let cancelled = false;

    const run = (): void => {
      void runCheck().catch(() => {
        // Sin cobertura no hay actualización que runCheck, y no es un error:
        // es el estado normal de una tablet en carretera.
      });
    };

    run();
    const timer = setInterval(
      () => {
        if (!cancelled) run();
      },
      Math.max(CHECK_INTERVAL_UNITS * resolveUnitMs(), MIN_CHECK_INTERVAL_MS)
    );

    // DOS señales a propósito, igual que en `nativeDurableStorage`: `resume` de
    // Capacitor está atado a `onResume()` de la Activity y apagar/encender la
    // pantalla no garantiza esa transición — se comprobó en campo que el aviso
    // no volvía así. `visibilitychange` del WebView es el refuerzo. Comprobar
    // de más no cuesta nada: `runCheck` ya se protege de la reentrada.
    const onVisible = (): void => {
      if (!cancelled && document.visibilityState === 'visible') run();
    };
    document.addEventListener('visibilitychange', onVisible);

    // Volver a primer plano es el mejor momento para runCheck: es cuando el
    // operario va a mirar la pantalla y cuando es más probable que haya
    // recuperado cobertura.
    let resumeHandle: { remove: () => Promise<void> } | null = null;
    if (isNativeApp()) {
      void CapacitorApp.addListener('resume', () => {
        if (!cancelled) run();
      }).then((handle) => {
        if (cancelled) {
          void handle.remove();
          return;
        }
        resumeHandle = handle;
      });
    }

    // Petición de comprobación inmediata (el SW detectó una versión nueva).
    const onCheckRequested = (): void => {
      if (!cancelled) run();
    };
    window.addEventListener(CHECK_UPDATE_EVENT, onCheckRequested);

    return () => {
      cancelled = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener(CHECK_UPDATE_EVENT, onCheckRequested);
      if (resumeHandle) void resumeHandle.remove();
    };
  }, [runCheck]);

  // El tramo se ha cerrado y había una actualización esperando: es el momento.
  // Y al revés — si el operario empieza un tramo con el aviso abierto, el aviso
  // se quita de en medio.
  useEffect(() => {
    if (!updatesEnabled()) return;
    if (hasActiveSegment) {
      closeIfMine();
      return;
    }
    if (statusRef.current) offerUpdate(statusRef.current);
  }, [hasActiveSegment, closeIfMine, offerUpdate]);

  // El operario cerró el aviso (la X, Escape o clic fuera). Redux es el único
  // sitio donde eso se puede ver desde aquí: el modal ya no es el nuestro.
  useEffect(() => {
    if (!openedByUs.current) return;
    if (currentModalId === modalIds.VERSION_UPDATE_MODAL) return;

    // 🔴 Solo cuenta si el modal se CERRÓ, no si otro lo reemplazó. `openModal`
    // sustituye el que hubiera, y los guardianes de GPS, de ubicación en
    // segundo plano y de permisos de fotos abren el suyo SOLOS: contarlos haría
    // que cinco avisos automáticos bloqueasen la tablet sin que el operario
    // cerrara nada, y silenciaría 12 h un aviso que ni llegó a leer.
    //
    // `closeIfMine` (tramo que empieza) y `onContinue` quedan fuera igual, por
    // el guard de arriba: bajan `openedByUs` antes de despachar.
    const loCerraron = currentModalId === null || currentModalId === undefined;
    if (loCerraron && offeredVersion.current) {
      const ahora = Date.now();
      const unitMs = resolveUnitMs();
      recordDismissal(offeredVersion.current, ahora, unitMs, RUNNING_WEB_VERSION);
      // El respaldo en memoria se apunta a la vez: si `localStorage` no pudo
      // escribir, este es el único silencio que queda.
      const { state } = readNag(offeredVersion.current, ahora, RUNNING_WEB_VERSION);
      quietUntilFallback.current[offeredVersion.current] =
        ahora + quietMsFor(state.dismissals, unitMs);
    }
    openedByUs.current = false;
    offeredVersion.current = null;
  }, [currentModalId]);

  // Si el componente que lo usa se desmonta, el aviso no debe quedarse en
  // pantalla sin nadie que lo gobierne.
  useEffect(() => closeIfMine, [closeIfMine]);
}

export default useAppUpdate;
